import type React from "react";

/**
 * The default map of supported typography types.
 */
export const TypographyTypeEnum = {
  Heading: "heading",
  Paragraph: "paragraph",
  Label: "label",
} as const;
export type TypographyTypeEnum =
  (typeof TypographyTypeEnum)[keyof typeof TypographyTypeEnum];

/**
 * The base typography configuration specifies the supported types and their supported variations.
 * Each type defines a set of supported sizes, weights, and variants.
 *
 * This type is meant to be used in a generic parameter, as a base for a specific typography configuration.
 */
export interface BaseTypographyConfig<
  Types extends string = TypographyTypeEnum
> {
  types: readonly Types[];
  supported: {
    [k in Types]: {
      size: readonly string[];
      weight: readonly string[];
      variant: readonly string[];
    };
  };
}

/**
 * The type representing a map of some typography type, mapping sizes to the
 * component that they should render.
 */
type TypographyComponentDefinitionBySizes<Sizes extends string> = {
  [k in Sizes | "default"]: React.ElementType;
};

/**
 * A convenience type for defining a typography component map, which allows
 * using a single component for any size.
 */
type TypographyComponentDefinition<Sizes extends string> =
  | React.ElementType
  | TypographyComponentDefinitionBySizes<Sizes>;

/**
 * The full typography configuration, including the default type and the
 * allowed variations for each type.
 *
 * This type is meant to be used in a generic parameter, as a base for a specific typography configuration.
 */
export type TypographyConfig<
  Base extends BaseTypographyConfig<string> = BaseTypographyConfig
> = Base & {
  default: {
    type: keyof Base["supported"];
    variations: {
      [Type in Base["types"][number]]: {
        size: Base["supported"][Type]["size"][number];
        weight: Base["supported"][Type]["weight"][number];
        variant: Base["supported"][Type]["variant"][number];
      };
    };
    components: {
      [Type in Base["types"][number]]: TypographyComponentDefinition<
        Base["supported"][Type]["size"][number]
      >;
    };
  };
};

/**
 * A generic type to use in a generic parameter for any typography configuration.
 */
export type TypographyGenericConfig = TypographyConfig<
  BaseTypographyConfig<string>
>;

/**
 * A helper to normalize a `TagDefinition` to a `TagDefinitionMap`, to make it
 * easier to lookup the default tag.
 */
type NormalizeComponentMap<
  Sizes extends string,
  Def extends TypographyComponentDefinition<Sizes>
> = Def extends React.ElementType
  ? {
      [k in Sizes]: Def;
    } & { default: Def }
  : Def;

/**
 * A type which resolves a given tag in a `TypographyComponentDefinitionBySizes` by size.
 *
 * The `Size` may be `never`, which resolves to the default tag.
 */
type LookupComponentBySize<
  Sizes extends string,
  Def extends TypographyComponentDefinitionBySizes<Sizes>,
  Size extends Sizes = never
> = [Size] extends [never] ? Def["default"] : Def[Size];

// Typography types

/**
 * Helper to statically resolve the supported types from any config.
 */
export type GetTypographySupportedTypesFromConfig<
  Config extends TypographyGenericConfig
> = Config["types"][number];

/**
 * Helper to dynamically resolve the supported types from any config.
 *
 * Should only be used in tests/stories.
 */
export const getTypographySupportedTypes: {
  (): readonly GetTypographySupportedTypesFromConfig<
    typeof typographyDefaultConfig
  >[];
  <Config extends TypographyGenericConfig>(
    config: Config
  ): readonly GetTypographySupportedTypesFromConfig<Config>[];
} = <Config extends TypographyGenericConfig>(
  config?: Config
): readonly GetTypographySupportedTypesFromConfig<Config>[] => {
  return (config ?? (typographyDefaultConfig as unknown as Config)).types;
};

/**
 * Helper to statically resolve the default type from any config.
 */
export type GetTypographyDefaultTypeFromConfig<
  Config extends TypographyGenericConfig
> = Config["default"]["type"];

/**
 * Helper to dynamically resolve the default type from any config.
 */
export const getTypographyDefaultType = <
  Config extends TypographyGenericConfig = typeof typographyDefaultConfig
>(
  config?: Config
): GetTypographyDefaultTypeFromConfig<Config> => {
  const c = config ?? (typographyDefaultConfig as unknown as Config);
  return c.default.type;
};

// Typography variations

/**
 * Helper to statically resolve the supported variations for a given type from
 * any config.
 */
export type GetTypographySupportedVariationsFromConfig<
  Config extends TypographyGenericConfig,
  Type extends GetTypographySupportedTypesFromConfig<Config>
> = {
  readonly size: Config["supported"][Type]["size"][number];
  readonly weight: Config["supported"][Type]["weight"][number];
  readonly variant: Config["supported"][Type]["variant"][number];
};

/**
 * Helper to transform a `Record<Key, Value>` to a `Record<Key, readonly Value[]>`.
 */
type ArrayValues<T> = {
  [k in keyof T]: readonly T[k][];
};

/**
 * Helper to dynamically resolve the supported variations for a given type from
 * any config.
 *
 * Should only be used in tests/stories.
 */
export const getTypographySupportedVariations: {
  <
    Type extends GetTypographySupportedTypesFromConfig<
      typeof typographyDefaultConfig
    >
  >(
    type: Type,
    config?: typeof typographyDefaultConfig
  ): ArrayValues<
    GetTypographySupportedVariationsFromConfig<
      typeof typographyDefaultConfig,
      Type
    >
  >;
  <
    Config extends TypographyGenericConfig,
    Type extends GetTypographySupportedTypesFromConfig<Config>
  >(
    type: Type,
    config: Config
  ): ArrayValues<GetTypographySupportedVariationsFromConfig<Config, Type>>;
} = <
  Type extends GetTypographySupportedTypesFromConfig<Config>,
  Config extends TypographyGenericConfig = typeof typographyDefaultConfig
>(
  type: Type,
  config?: Config
): ArrayValues<GetTypographySupportedVariationsFromConfig<Config, Type>> => {
  const c = config ?? (typographyDefaultConfig as unknown as Config);
  return c.supported[type];
};

/**
 * Helper to statically resolve the default variation for a given type from
 * any config.
 */
export type GetTypographyDefaultVariationFromConfig<
  Config extends TypographyGenericConfig,
  Type extends GetTypographySupportedTypesFromConfig<Config>
> = Config["default"]["variations"][Type];

/**
 * Helper to dynamically resolve the default variation for a given type from
 * any config.
 *
 * Should only be used in tests/stories.
 */
export const getTypographyDefaultVariation = <
  Config extends TypographyGenericConfig = typeof typographyDefaultConfig,
  Type extends GetTypographySupportedTypesFromConfig<Config> = GetTypographyDefaultTypeFromConfig<Config>
>(
  type: Type,
  config?: Config
): GetTypographyDefaultVariationFromConfig<Config, Type> => {
  const c = config ?? (typographyDefaultConfig as unknown as Config);
  return c.default.variations[type] as GetTypographyDefaultVariationFromConfig<
    Config,
    Type
  >;
};

// Typography components

export type GetTypographyDefaultComponentFromConfig<
  Config extends TypographyGenericConfig,
  Type extends GetTypographySupportedTypesFromConfig<Config>,
  Size extends GetTypographySupportedVariationsFromConfig<Config, Type>["size"]
> = LookupComponentBySize<
  GetTypographySupportedVariationsFromConfig<Config, Type>["size"],
  NormalizeComponentMap<
    GetTypographySupportedVariationsFromConfig<Config, Type>["size"],
    Config["default"]["components"][Type]
  >,
  Size
>;

export const getTypographyDefaultComponentFromConfig = <
  Config extends TypographyGenericConfig,
  Type extends GetTypographySupportedTypesFromConfig<Config>,
  Size extends GetTypographySupportedVariationsFromConfig<Config, Type>["size"]
>(
  config: Config,
  type: Type,
  size?: Size
): React.ElementType => {
  type Return = GetTypographyDefaultComponentFromConfig<Config, Type, Size>;
  const typemap = config.default.components[type];
  if (typeof typemap === "string" || typeof typemap === "function") {
    return typemap as Return;
  } else if (size == null) {
    return typemap.default as Return;
  } else {
    return typemap[size] as Return;
  }
};

// Default configuration

const baseDefaultTypographyConfig = {
  types: [
    TypographyTypeEnum.Heading,
    TypographyTypeEnum.Paragraph,
    TypographyTypeEnum.Label,
  ],
  supported: {
    [TypographyTypeEnum.Heading]: {
      size: ["xxl", "xl", "lg", "md", "sm", "xs", "xxs"],
      weight: ["light", "normal"],
      variant: ["primary"],
    },
    [TypographyTypeEnum.Paragraph]: {
      size: ["xl", "lg", "md", "sm", "xs"],
      weight: ["normal", "strong"],
      variant: ["primary", "secondary", "custom"],
    },
    [TypographyTypeEnum.Label]: {
      size: ["xl", "lg", "md", "sm", "xs"],
      weight: ["normal", "strong"],
      variant: ["primary", "secondary", "custom"],
    },
  },
} as const satisfies BaseTypographyConfig<string>;

export const typographyDefaultConfig = {
  ...baseDefaultTypographyConfig,
  default: {
    type: TypographyTypeEnum.Paragraph,
    variations: {
      [TypographyTypeEnum.Heading]: {
        size: "md",
        weight: "normal",
        variant: "primary",
      },
      [TypographyTypeEnum.Paragraph]: {
        size: "md",
        weight: "normal",
        variant: "primary",
      },
      [TypographyTypeEnum.Label]: {
        size: "md",
        weight: "normal",
        variant: "primary",
      },
    },
    components: {
      [TypographyTypeEnum.Heading]: {
        xxl: "h1",
        xl: "h2",
        lg: "h3",
        md: "h4",
        sm: "h5",
        xs: "h6",
        xxs: "h6",
        default: "h1",
      },
      [TypographyTypeEnum.Paragraph]: "p",
      [TypographyTypeEnum.Label]: "span",
    },
  } as const,
} as const satisfies TypographyConfig<typeof baseDefaultTypographyConfig>;
